import { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@repo/ui/components/dialog';
import { Drawer } from '@/components/ui/drawer';
import { Button } from '@repo/ui/components/button';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@repo/ui/hooks/use-mobile';

export type MatchResult = {
  set1: { winner: string; score: [number, number]; gameId: string };
  set2: { winner: string; score: [number, number]; gameId: string };
  supertiebreak: { winner: string; score: [number, number]; gameId: string };
};

interface MatchResultModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  opponent1: { id: string; name: string };
  opponent2: { id: string; name: string };
  matchGames: { id: string }[]; // [set1, set2, supertiebreak]
  onSubmit: (result: MatchResult) => Promise<void>;
}

type Step = 1 | 2 | 3;

interface SetScore {
  opponent1: number | '';
  opponent2: number | '';
}

function validateSetScore(score1: number, score2: number): string | null {
  if (score1 < 0 || score1 > 6 || score2 < 0 || score2 > 6) {
    return 'Los puntajes deben estar entre 0 y 6';
  }
  if (score1 === score2) {
    return 'Los puntajes no pueden ser iguales';
  }
  const winner = score1 > score2 ? score1 : score2;
  if (winner !== 6) {
    return 'El ganador debe tener 6 puntos';
  }
  const loser = score1 < score2 ? score1 : score2;
  if (loser > 5) {
    return 'El perdedor no puede tener más de 5 puntos';
  }
  return null;
}

function validateSuperTiebreak(score1: number, score2: number): string | null {
  if (score1 < 0 || score2 < 0 || !Number.isInteger(score1) || !Number.isInteger(score2)) {
    return 'Los puntajes deben ser números enteros positivos';
  }
  if (score1 === score2) {
    return 'Los puntajes no pueden ser iguales';
  }
  const winner = Math.max(score1, score2);
  const loser = Math.min(score1, score2);

  if (winner < 11) {
    return 'El ganador debe tener al menos 11 puntos';
  }

  if (loser >= 10 && winner < loser + 2) {
    return 'Si ambos alcanzan 10 puntos, se debe ganar por diferencia de 2 puntos';
  }

  if (loser < 10 && winner !== 11) {
    return 'Si el perdedor tiene menos de 10 puntos, el ganador debe tener exactamente 11';
  }

  return null;
}

function getWinner(
  score: SetScore,
  opponent1: { id: string; name: string },
  opponent2: { id: string; name: string }
): string | null {
  if (score.opponent1 === '' || score.opponent2 === '') {
    return null;
  }
  return Number(score.opponent1) > Number(score.opponent2) ? opponent1.id : opponent2.id;
}

function SetInput({
  opponent1Name,
  opponent2Name,
  score,
  onChange,
  error,
}: {
  opponent1Name: string;
  opponent2Name: string;
  score: SetScore;
  onChange: (score: SetScore) => void;
  error: string | null;
}) {
  const handleOpponent1Change = (value: string) => {
    const numValue = value === '' ? '' : isNaN(parseInt(value, 10)) ? '' : parseInt(value, 10);
    if (numValue === '') {
      onChange({ opponent1: '', opponent2: score.opponent2 });
      return;
    }
    const num = Number(numValue);
    if (num === 6) {
      // Si pone 6, deshabilitar 6 para el otro (pero mantener su valor si ya tiene uno válido)
      onChange({ opponent1: 6, opponent2: score.opponent2 });
    } else if (num >= 0 && num <= 5) {
      // Si pone un número distinto a 6, autoseleccionar 6 para el otro
      onChange({ opponent1: num, opponent2: 6 });
    } else {
      onChange({ opponent1: numValue, opponent2: score.opponent2 });
    }
  };

  const handleOpponent2Change = (value: string) => {
    const numValue = value === '' ? '' : isNaN(parseInt(value, 10)) ? '' : parseInt(value, 10);
    if (numValue === '') {
      onChange({ opponent1: score.opponent1, opponent2: '' });
      return;
    }
    const num = Number(numValue);
    if (num === 6) {
      // Si pone 6, deshabilitar 6 para el otro
      onChange({ opponent1: score.opponent1, opponent2: 6 });
    } else if (num >= 0 && num <= 5) {
      // Si pone un número distinto a 6, autoseleccionar 6 para el otro
      onChange({ opponent1: 6, opponent2: num });
    } else {
      onChange({ opponent1: score.opponent1, opponent2: numValue });
    }
  };

  const opponent1Value = Number(score.opponent1);
  const opponent2Value = Number(score.opponent2);
  const opponent1Disabled = opponent2Value === 6 ? [6] : [];
  const opponent2Disabled = opponent1Value === 6 ? [6] : [];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>{opponent1Name}</Label>
          <div className="grid grid-cols-4 gap-1">
            {[0, 1, 2, 3, 4, 5, 6].map((num) => (
              <Button
                key={num}
                type="button"
                variant={score.opponent1 === num ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleOpponent1Change(String(num))}
                disabled={opponent1Disabled.includes(num)}
                className="w-full"
              >
                {num}
              </Button>
            ))}
          </div>
        </div>
        <div className="space-y-2">
          <Label>{opponent2Name}</Label>
          <div className="grid grid-cols-4 gap-1">
            {[0, 1, 2, 3, 4, 5, 6].map((num) => (
              <Button
                key={num}
                type="button"
                variant={score.opponent2 === num ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleOpponent2Change(String(num))}
                disabled={opponent2Disabled.includes(num)}
                className="w-full"
              >
                {num}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}

function SuperTiebreakInput({
  opponent1Name,
  opponent2Name,
  score,
  onChange,
  error,
}: {
  opponent1Name: string;
  opponent2Name: string;
  score: SetScore;
  onChange: (score: SetScore) => void;
  error: string | null;
}) {
  const handleOpponent1Change = (value: string) => {
    const numValue = value === '' ? '' : isNaN(parseInt(value, 10)) ? '' : parseInt(value, 10);
    if (numValue === '') {
      onChange({ opponent1: '', opponent2: score.opponent2 });
      return;
    }
    const num = Number(numValue);

    // Si pone menos de 11, autoseleccionar 11 para el otro
    if (num < 11) {
      onChange({ opponent1: num, opponent2: 11 });
    }
    // Si pone 11 o más, mantener el valor del otro (no autoseleccionar)
    else {
      onChange({ opponent1: num, opponent2: score.opponent2 });
    }
  };

  const handleOpponent2Change = (value: string) => {
    const numValue = value === '' ? '' : isNaN(parseInt(value, 10)) ? '' : parseInt(value, 10);
    if (numValue === '') {
      onChange({ opponent1: score.opponent1, opponent2: '' });
      return;
    }
    const num = Number(numValue);

    // Si pone menos de 11, autoseleccionar 11 para el otro
    if (num < 11) {
      onChange({ opponent1: 11, opponent2: num });
    }
    // Si pone 11 o más, mantener el valor del otro (no autoseleccionar)
    else {
      onChange({ opponent1: score.opponent1, opponent2: num });
    }
  };

  const opponent1Value = Number(score.opponent1);
  const opponent2Value = Number(score.opponent2);

  // Generar opciones de botones (0-20 debería cubrir la mayoría de casos)
  const options = Array.from({ length: 21 }, (_, i) => i);

  const getButtonDisabled = (value: number, isOpponent1: boolean) => {
    const otherValue = isOpponent1 ? opponent2Value : opponent1Value;

    // Si el otro tiene más de 11 y este tiene menos de 10, deshabilitar
    // (si el perdedor tiene menos de 10, el ganador debe tener exactamente 11)
    if (otherValue > 11 && value < 10) {
      return true;
    }

    // Si el otro tiene menos de 10 y este tiene más de 11, deshabilitar (debería terminar en 11)
    if (otherValue < 10 && value > 11) {
      return true;
    }

    // Si el otro tiene 10 y este tiene menos de 10, deshabilitar (el otro debe ser al menos 10)
    if (otherValue === 10 && value < 10) {
      return true;
    }

    return false;
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>{opponent1Name}</Label>
          <div className="grid grid-cols-5 gap-1 max-h-48 overflow-y-auto">
            {options.map((num) => {
              const disabled = getButtonDisabled(num, true);
              return (
                <Button
                  key={num}
                  type="button"
                  variant={score.opponent1 === num ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleOpponent1Change(String(num))}
                  disabled={disabled}
                  className="text-xs w-full"
                >
                  {num}
                </Button>
              );
            })}
          </div>
        </div>
        <div className="space-y-2">
          <Label>{opponent2Name}</Label>
          <div className="grid grid-cols-5 gap-1 max-h-48 overflow-y-auto">
            {options.map((num) => {
              const disabled = getButtonDisabled(num, false);
              return (
                <Button
                  key={num}
                  type="button"
                  variant={score.opponent2 === num ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleOpponent2Change(String(num))}
                  disabled={disabled}
                  className="text-xs w-full"
                >
                  {num}
                </Button>
              );
            })}
          </div>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        El ganador debe tener al menos 11 puntos. Si ambos alcanzan 10 puntos, se debe ganar por
        diferencia de 2 puntos.
      </p>
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}

export function MatchResultModal({
  open,
  onOpenChange,
  opponent1,
  opponent2,
  matchGames,
  onSubmit,
}: MatchResultModalProps) {
  const isMobile = useIsMobile();
  const [step, setStep] = useState<Step>(1);
  const [loading, setLoading] = useState(false);
  const [set1Score, setSet1Score] = useState<SetScore>({ opponent1: '', opponent2: '' });
  const [set2Score, setSet2Score] = useState<SetScore>({ opponent1: '', opponent2: '' });
  const [tiebreakScore, setTiebreakScore] = useState<SetScore>({ opponent1: '', opponent2: '' });
  const [set1Error, setSet1Error] = useState<string | null>(null);
  const [set2Error, setSet2Error] = useState<string | null>(null);
  const [tiebreakError, setTiebreakError] = useState<string | null>(null);

  const winner1 = useMemo(
    () => getWinner(set1Score, opponent1, opponent2),
    [set1Score, opponent1, opponent2]
  );
  const winner2 = useMemo(
    () => getWinner(set2Score, opponent1, opponent2),
    [set2Score, opponent1, opponent2]
  );

  useEffect(() => {
    if (!open) {
      setStep(1);
      setSet1Score({ opponent1: '', opponent2: '' });
      setSet2Score({ opponent1: '', opponent2: '' });
      setTiebreakScore({ opponent1: '', opponent2: '' });
      setSet1Error(null);
      setSet2Error(null);
      setTiebreakError(null);
    }
  }, [open]);

  const handleSet1Next = () => {
    if (set1Score.opponent1 === '' || set1Score.opponent2 === '') {
      setSet1Error('Por favor ingrese ambos puntajes');
      return;
    }
    const error = validateSetScore(Number(set1Score.opponent1), Number(set1Score.opponent2));
    if (error) {
      setSet1Error(error);
      return;
    }
    setSet1Error(null);
    setStep(2);
  };

  const handleSet2Next = () => {
    if (set2Score.opponent1 === '' || set2Score.opponent2 === '') {
      setSet2Error('Por favor ingrese ambos puntajes');
      return;
    }
    const error = validateSetScore(Number(set2Score.opponent1), Number(set2Score.opponent2));
    if (error) {
      setSet2Error(error);
      return;
    }
    setSet2Error(null);

    if (winner1 !== winner2) {
      setStep(3);
    } else {
      handleSubmit(false);
    }
  };

  const handleTiebreakNext = () => {
    if (tiebreakScore.opponent1 === '' || tiebreakScore.opponent2 === '') {
      setTiebreakError('Por favor ingrese ambos puntajes');
      return;
    }
    const error = validateSuperTiebreak(
      Number(tiebreakScore.opponent1),
      Number(tiebreakScore.opponent2)
    );
    if (error) {
      setTiebreakError(error);
      return;
    }
    setTiebreakError(null);
    handleSubmit(true);
  };

  const handleSubmit = async (needsTiebreak: boolean) => {
    setLoading(true);
    try {
      // Validate that matchGames has at least 3 elements
      if (!matchGames || matchGames.length < 3) {
        throw new Error(
          `Invalid match games data: expected at least 3 games, but received ${
            matchGames?.length ?? 0
          }`
        );
      }

      const result: MatchResult = {
        set1: {
          winner: winner1!,
          score: [Number(set1Score.opponent1), Number(set1Score.opponent2)],
          gameId: matchGames[0].id,
        },
        set2: {
          winner: winner2!,
          score: [Number(set2Score.opponent1), Number(set2Score.opponent2)],
          gameId: matchGames[1].id,
        },
        supertiebreak: needsTiebreak
          ? {
              winner: getWinner(tiebreakScore, opponent1, opponent2)!,
              score: [Number(tiebreakScore.opponent1), Number(tiebreakScore.opponent2)],
              gameId: matchGames[2].id,
            }
          : {
              winner: winner1!,
              score: [0, 0],
              gameId: matchGames[2].id,
            },
      };

      await onSubmit(result);
      onOpenChange(false);
    } catch (error) {
      console.error('Error submitting match result:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
    const needsStep3 = step >= 2 && winner1 && winner2 && winner1 !== winner2;
    const maxStep = needsStep3 ? 3 : 2;

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">
              Paso {step} de {maxStep}
            </p>
          </div>
          <div className="flex gap-1">
            {[1, 2, 3].map((s) => {
              if (s > maxStep) return null;

              return (
                <div
                  key={s}
                  className={cn(
                    'h-2 w-8 rounded-full transition-colors',
                    s === step ? 'bg-primary' : s < step ? 'bg-primary/50' : 'bg-muted'
                  )}
                />
              );
            })}
          </div>
        </div>

        {step === 1 && (
          <div className="space-y-4">
            <h3 className="font-medium">Set 1</h3>
            <SetInput
              opponent1Name={opponent1.name}
              opponent2Name={opponent2.name}
              score={set1Score}
              onChange={setSet1Score}
              error={set1Error}
            />
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <h3 className="font-medium">Set 2</h3>
            <SetInput
              opponent1Name={opponent1.name}
              opponent2Name={opponent2.name}
              score={set2Score}
              onChange={setSet2Score}
              error={set2Error}
            />
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <h3 className="font-medium">Super Tiebreak</h3>
            <SuperTiebreakInput
              opponent1Name={opponent1.name}
              opponent2Name={opponent2.name}
              score={tiebreakScore}
              onChange={setTiebreakScore}
              error={tiebreakError}
            />
          </div>
        )}

        <div className="flex gap-2 justify-end">
          {step > 1 && (
            <Button
              type="button"
              variant="outline"
              onClick={() => setStep((s) => (s - 1) as Step)}
              disabled={loading}
            >
              Anterior
            </Button>
          )}
          {step === 1 && (
            <Button type="button" onClick={handleSet1Next} disabled={loading}>
              Siguiente
            </Button>
          )}
          {step === 2 && (
            <Button type="button" onClick={handleSet2Next} disabled={loading}>
              {winner1 && winner2 && winner1 !== winner2 ? 'Siguiente' : 'Guardar'}
            </Button>
          )}
          {step === 3 && (
            <Button type="button" onClick={handleTiebreakNext} disabled={loading}>
              {loading ? 'Guardando...' : 'Guardar'}
            </Button>
          )}
        </div>
      </div>
    );
  };

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <div className="pb-4">
          <h2 className="text-lg font-semibold mb-4">Resultado del Partido</h2>
          {renderContent()}
        </div>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Resultado del Partido</DialogTitle>
        </DialogHeader>
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
}
